import { message } from "antd";
import { formatDistance } from "date-fns";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  DAP_FIELDS,
  NoteType,
  SOAP_FIELDS,
  getCptCodeByPsychotherapyMinutes,
  getCptCodeLabelByPsychotherapyMinutes,
  getCptSuggestedCode,
  getNotesType,
  showAddon,
  showCptCodes,
} from "../domain/notes.domain";
import { UserInfoProps } from "../slices/authSlice";
import { TClientForm, TDiagnosticCode, TNotes, TProceduralCode } from "../types/index.type";
import { EventType, NoteSection, trackEvent } from "./analytics";

pdfMake.vfs = pdfFonts.vfs;

export const convertPlainTextToHtml = (text: string): string => {
  if (!text) {
    return "";
  }

  return text
    .split("\n")
    .map((line: string) => {
      if (line.trim() === "") {
        return "<p><br></p>";
      } else {
        return `<p>${line}</p>`;
      }
    })
    .join("");
};

export function formatDuration(seconds: number) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const formattedHours = hrs.toString().padStart(2, "0");
  const formattedMinutes = mins.toString().padStart(2, "0");
  const formattedSeconds = secs.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function formatTimeToSecondsMinsHoursFromSeconds(seconds: number) {
  if (isNaN(seconds) || !seconds) {
    return "0 sec";
  }
  if (seconds < 60) {
    return `${seconds} sec`;
  } else if (seconds >= 60 && seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min ${secs} sec`;
  } else {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs} hrs ${mins} min ${secs} sec`;
  }
}

export const showPaymentModal = (userInfo: UserInfoProps) => {
  return !userInfo.is_paying && userInfo.total_recordings >= userInfo.free_recordings_limit;
};

export const copyContent = (
  notesDetails: TNotes,
  htmlContent: string,
  section: NoteSection,
  plainTextContent = "",
): void => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: section,
      recordingId: notesDetails?.id,
    });
    const copyToClipboard = async (htmlContent: string, plainTextContent = "") => {
      try {
        if (plainTextContent) {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([htmlContent], { type: "text/html" }),
              "text/plain": new Blob([plainTextContent], {
                type: "text/plain",
              }),
            }),
          ]);
        } else {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([htmlContent], { type: "text/html" }),
              "text/plain": new Blob([htmlContent], { type: "text/plain" }),
            }),
          ]);
        }
        console.log("Content copied to clipboard successfully!");
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    };

    copyToClipboard(htmlContent, plainTextContent);
  }
};

export const copySectionContent = (notesDetails: TNotes, text: string, section: NoteSection) => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: section,
      recordingId: notesDetails?.id,
    });
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  }
};

export const copySessionDetails = (notesDetails: TNotes) => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: NoteSection.SESSION_DETAILS,
      recordingId: notesDetails?.id,
    });
    let copiedText = "";

    if (notesDetails?.location_type != null || notesDetails?.duration_type != null) {
      const details = ["Session Details:"];

      if (notesDetails?.location_type != null) {
        details.push(`Location: ${notesDetails.location_type === 0 ? "Telehealth" : "In-person"}`);
      }

      if (notesDetails?.duration_type != null) {
        details.push(`CPT Code: ${durationTextFromType(notesDetails.duration_type)}\n`);
      }

      copiedText = `${details.join("\n\n")}\n\n`;
    }
    navigator.clipboard.writeText(copiedText);
    message.success("Copied to clipboard");
  }
};

export function formatTimeInLocalTimezone(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
}

export const copyAddonDetails = (recordingDetails: TNotes) => {
  const psychotherapyMinutes = recordingDetails.psychotherapy_minutes || 0;
  const psychotherapyExplanation = recordingDetails.psychotherapy_minutes_explanation || "";

  const details = ["Psychotherapy Add-on:"];
  let copiedText = "";

  if (showAddon(recordingDetails)) {
    const cptCodeLabel = getCptCodeLabelByPsychotherapyMinutes(psychotherapyMinutes, recordingDetails);
    details.push(`Add-On CPT Code: ${cptCodeLabel}`);
  }

  if (psychotherapyMinutes) {
    details.push(`Psychotherapy Minutes: ${psychotherapyMinutes}`);
  }

  if (psychotherapyExplanation) {
    details.push(`Explanation: ${psychotherapyExplanation}`);
  }

  // Add psychotherapy timing information
  if (recordingDetails.stats_transcribe_start_at && psychotherapyMinutes && psychotherapyMinutes > 0) {
    const endTime = new Date(recordingDetails.stats_transcribe_start_at);
    const startTime = new Date(endTime.getTime() - psychotherapyMinutes * 60 * 1000);

    details.push(
      `In this session, psychotherapy started at ${formatTimeInLocalTimezone(startTime.toISOString())} and ended at ${formatTimeInLocalTimezone(recordingDetails.stats_transcribe_start_at)}.`,
    );
  }

  copiedText = details.join("\n\n");

  navigator.clipboard.writeText(copiedText);
  message.success("Copied to clipboard");
};

export const copyCPTDetails = (recordingDetails: TNotes) => {
  const CPTExplanation = recordingDetails.suggested_cpt_code_explanation || "";

  const details = ["Suggested CPT code:"];
  let copiedText = "";

  if (showCptCodes(recordingDetails)) {
    const cptCodeLabel = getCptSuggestedCode(recordingDetails);
    details.push(`CPT Code: ${cptCodeLabel}`);
  }

  if (CPTExplanation) {
    details.push(`Explanation: ${CPTExplanation}`);
  }

  copiedText = details.join("\n\n");

  navigator.clipboard.writeText(copiedText);
  message.success("Copied to clipboard");
};

// Helper function to check if procedural code is a psychotherapy code
const isDefaultPsychotherapyCode = (code: TProceduralCode): boolean => {
  return code.additional_fields?.psychotherapy_duration_minutes !== undefined;
};

// Helper function to get the display code for procedural codes
const getDisplayCode = (code: TProceduralCode): string => {
  if (isDefaultPsychotherapyCode(code)) {
    if (getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)) {
      return getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)?.toString() || "";
    }
    // This means the psychotherapy minutes is less than 16 minutes, so we use the default code
    return "90833";
  }
  return code.code;
};

// Helper function to add additional explanation for psychotherapy codes
const addAdditionalExplanation = (code: TProceduralCode, notesDetails: TNotes): string => {
  // Check if this code has psychotherapy_duration_minutes in additional_fields
  const psychotherapyMinutes = code.additional_fields?.psychotherapy_duration_minutes;

  // Add timing information if available
  if (notesDetails?.stats_transcribe_start_at && psychotherapyMinutes && psychotherapyMinutes > 0) {
    const endTime = new Date(notesDetails.stats_transcribe_start_at);
    const startTime = new Date(endTime.getTime() - psychotherapyMinutes * 60 * 1000);

    return `\n\nIn this session, psychotherapy started at ${formatTimeInLocalTimezone(startTime.toISOString())} and ended at ${formatTimeInLocalTimezone(notesDetails.stats_transcribe_start_at)}.`;
  }

  return "";
};

// Build procedural codes text matching ProceduralCoding component formatting
export const buildProceduralCodesText = (notesDetails: TNotes): string => {
  if (!notesDetails?.procedural_codes || notesDetails.procedural_codes.length === 0) {
    return "";
  }

  const details = ["Procedural Codes:"];

  const codesText = notesDetails.procedural_codes
    .map((code) => {
      const codeDetails = [];
      const displayCode = getDisplayCode(code);

      // Add CPT Code
      codeDetails.push(`CPT Code: ${displayCode}`);

      // Add psychotherapy minutes for psychotherapy codes OR short description for other codes
      if (isDefaultPsychotherapyCode(code)) {
        if (code.additional_fields?.psychotherapy_duration_minutes !== undefined) {
          codeDetails.push(`Psychotherapy mins: ${code.additional_fields.psychotherapy_duration_minutes}`);
        }
      }

      // Add explanation (rationale + additional timing info for psychotherapy)
      const fullExplanation = code.rationale + addAdditionalExplanation(code, notesDetails);
      codeDetails.push(`Explanation: ${fullExplanation}`);

      return codeDetails.join("\n");
    })
    .join("\n\n");

  if (codesText) {
    details.push(codesText);
    return `\n\n${details.join("\n\n")}\n\n`;
  }

  return "";
};

export const buildDiagnosticCodesText = (notesDetails: TNotes): string => {
  if (!notesDetails?.diagnostic_codes || notesDetails.diagnostic_codes.length === 0) {
    return "";
  }

  const details = ["Diagnostic Codes (ICD-10):"];

  const codesText = notesDetails.diagnostic_codes
    .map((code: TDiagnosticCode) => {
      const codeDetails = [`ICD-10 Code: ${code.code}`];

      if (code.short_description) {
        codeDetails.push(`Description: ${code.short_description}`);
      }

      const confidence = code.additional_fields?.confidence;
      if (confidence) {
        codeDetails.push(`Confidence: ${confidence}`);
      }

      if (code.rationale) {
        codeDetails.push(`Rationale: ${code.rationale}`);
      }

      return codeDetails.join("\n");
    })
    .join("\n\n");

  if (codesText) {
    details.push(codesText);
    return `\n\n${details.join("\n\n")}\n\n`;
  }

  return "";
};

export const durationTimeFromType = (type: string | number) => {
  switch (parseInt(type as string)) {
    case 0:
    case 13:
      return "30 min";
    case 1:
    case 14:
      return "45 minutes";
    case 2:
    case 15:
      return "60 minutes";
    case 3:
      return "15-29 minutes";
    case 4:
      return "30-44 minutes";
    case 5:
      return "45-59 minutes";
    case 6:
      return "60-74 minutes";
    case 7:
      return "10-19 minutes";
    case 8:
      return "20-29 minutes";
    case 9:
      return "30-39 minutes";
    case 10:
      return "40-54 minutes";
    case 11:
      return "45-60 minutes";
    case 12:
      return "45-60 minutes";
    case 16:
      return "60 minutes";
    case 17:
      return "45-60 minutes";
    case 18:
      return "45-60 minutes";
    case 19:
      return "45-60 minutes";
    default:
      return "-";
  }
};

export const durationTextFromType = (type: string | number): string => {
  const parsedType = parseInt(type as string);

  switch (parsedType) {
    case 0:
    case 13:
      return "90832: Psychotherapy, 30 minutes";
    case 1:
    case 14:
      return "90834: Psychotherapy, 45 minutes";
    case 2:
    case 15:
      return "90837: Psychotherapy, 60 minutes";
    case 3:
      return "99202: New patient, 15-29 minutes";
    case 4:
      return "99203: New patient, 30-44 minutes";
    case 5:
      return "99204: New Patient, 45-59 minutes";
    case 6:
      return "99205: New Patient, 60-74 minutes";
    case 7:
      return "99212: Established Patient, 10-19 minutes";
    case 8:
      return "99213: Established Patient, 20-29 minutes";
    case 9:
      return "99214: Established Patient, 30-39 minutes";
    case 10:
      return "99215: Established Patient Visit, 40-54 minutes";
    case 11:
      return "90791: Psychiatric Diagnostic Evaluation";
    case 12:
      return "90792: Psychiatric Diagnostic Evaluation Incl. Medical Services";
    case 16:
      return "90839: Psychotherapy for crisis situations, first 60 minutes";
    case 17:
      return "90846: Family Psychotherapy without patient present, 50 minutes";
    case 18:
      return "90847: Family Psychotherapy with patient present, 50 minutes";
    case 19:
      return "90853: Group Psychotherapy, not family-related";
    default:
      return "-";
  }
};

export const sortClientsByName = (clients: TClientForm[]) => {
  return clients.sort((a: any, b: any) => a.name.localeCompare(b.name));
};

export const getFirstLetterOfNameAndSurname = (name: string) => {
  if (!name) return "";
  const nameArray = name.split(" ");
  const initials = nameArray.length > 1 ? `${nameArray[0].charAt(0)}${nameArray[1].charAt(0)}` : nameArray[0].charAt(0);
  return initials.toUpperCase();
};

export function formatDistanceFromInputDateTimeToNow(dateTimeString: string) {
  return formatDistance(new Date(dateTimeString), new Date(), {
    addSuffix: true,
  });
}

export const htmlToText = (htmlContent: string): string => {
  return htmlContent
    .replace(/<br>/g, "\n") // replace <br> with newline
    .replace(/<\/p>/g, "\n") // replace </p> with newline
    .replace(/&nbsp;/g, " ") // replace &nbsp; with space
    .replace(/<[^>]*>?/gm, ""); // remove any other html tags
};

export const replaceNewLineWithBreakTag = (text: string): string => {
  return text.replace(/\n/g, "<br>");
};

export const copyContentAndNotify = (notesDetails: TNotes, htmlContent: string, plainTextContent: string) => {
  copyContent(notesDetails, htmlContent, NoteSection.ALL, plainTextContent);
};

export const generateHtmlAndPlainText = (copiedText: string, additionalText: string, addOnDetails: string) => {
  const htmlToCopy = replaceNewLineWithBreakTag(copiedText + additionalText + addOnDetails);
  const plainTextToCopy = `${copiedText}\n\n${additionalText}\n\n${addOnDetails}`;
  return { htmlToCopy, plainTextToCopy };
};

export const copyNote = (notesDetails: TNotes, copiedText: string, addOnDetails: string) => {
  switch (notesDetails.note_type) {
    case 0: // SOAP
      copiedText += `Subjective: \n${notesDetails.subject}\n\nObjective: \n${notesDetails.objective}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
      if (addOnDetails) {
        copiedText += `\n\n${addOnDetails}`;
      }
      copyContentAndNotify(notesDetails, replaceNewLineWithBreakTag(copiedText), copiedText);
      break;
    case 1: // Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(notesDetails.intake_note)}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 2: // DAP Intake
      copiedText += `Data: \n${notesDetails.data}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
      if (addOnDetails) {
        copiedText += `\n\n${addOnDetails}`;
      }
      copyContentAndNotify(notesDetails, replaceNewLineWithBreakTag(copiedText), copiedText);
      break;
    case 4: // EAP Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.eap_intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(notesDetails.eap_intake_note)}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 5: // Psych Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.psych_intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.psych_intake_note)} ${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 6: // SOL Psych Intake
      const solPsychIntake = generateHtmlAndPlainText(
        copiedText,
        notesDetails?.sol_psych_intake_note || "",
        addOnDetails,
      );
      copyContentAndNotify(notesDetails, solPsychIntake.htmlToCopy, solPsychIntake.plainTextToCopy);
      return;
    case 7: // SOL Psych Followup
      const solPsychFollowup = generateHtmlAndPlainText(
        copiedText,
        notesDetails?.sol_psych_followup_note || "",
        addOnDetails,
      );
      copyContentAndNotify(notesDetails, solPsychFollowup.htmlToCopy, solPsychFollowup.plainTextToCopy);
      return;
    case 8: // SOL Therapy Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails?.sol_therapy_intake_note + addOnDetails),
        `${copiedText}\n\n${notesDetails?.sol_therapy_intake_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 9:
    case 10: // SOL Therapy Followup
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails?.sol_therapy_followup_note + addOnDetails),
        `${copiedText}\n\n${notesDetails?.sol_therapy_followup_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 11:
      const htmlToCopy = replaceNewLineWithBreakTag(copiedText + notesDetails?.psych_followup_note + addOnDetails);
      const plainTextToCopy = `${copiedText}\n\n${notesDetails?.psych_followup_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`;
      copyContent(notesDetails, htmlToCopy, NoteSection.ALL, plainTextToCopy);
      message.success("All content copied to clipboard");
      return;
    case 12:
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.document + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.document + addOnDetails)}`,
      );
      return;
    default: // Custom Note
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.document + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.document + addOnDetails)}}`,
      );
      return;
  }
};

export const generateHTMLForPDF = (notesDetails: TNotes) => {
  let document = "";
  if (notesDetails.note_type === NoteType.SOAP) {
    SOAP_FIELDS.forEach((field) => {
      document += `<div class="pdf-section"><strong>${field.title}:</strong><br/>${replaceNewLineWithBreakTag(notesDetails[field.object as keyof TNotes] as string)}</div>`;
    });
  }
  if (notesDetails.note_type === NoteType.DAP) {
    DAP_FIELDS.forEach((field) => {
      document += `<div class="pdf-section"><strong>${field.title}:</strong><br/>${replaceNewLineWithBreakTag(notesDetails[field.object as keyof TNotes] as string)}</div>`;
    });
  }
  if (notesDetails.note_type === NoteType.Intake) {
    document += `<div class="pdf-section"><strong>Intake:</strong><br/>${replaceNewLineWithBreakTag(notesDetails.intake_note)}</div>`;
  }
  if (notesDetails.note_type === NoteType.EAP_INTAKE) {
    document += `<div class="pdf-section"><strong>EAP Intake:</strong><br/>${replaceNewLineWithBreakTag(notesDetails.eap_intake_note)}</div>`;
  }
  if (notesDetails.note_type === NoteType.PSYCHIATRIC_INTAKE) {
    document += `<div class="pdf-section"><strong>Psychatric Intake:</strong><br/>${replaceNewLineWithBreakTag(notesDetails.psych_intake_note)}</div>`;
  }
  if (notesDetails.note_type === NoteType.EMDR) {
    document += `<div class="pdf-section"><strong>EMDR:</strong><br/>${replaceNewLineWithBreakTag(notesDetails.emdr_note)}</div>`;
  }
  if (notesDetails.note_type === NoteType.SOL_PSYCH_INTAKE) {
    document += `<div class="pdf-section"><strong>Psychiatric Intake:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.sol_psych_intake_note || "")}</div>`;
  }
  if (notesDetails.note_type === NoteType.PSYCHIATRIC_FOLLOW_UP) {
    document += `<div class="pdf-section"><strong>Psychiatric Follow-up:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.psych_followup_note || "")}</div>`;
  }
  if (notesDetails.note_type === NoteType.THERAPY_INTAKE) {
    document += `<div class="pdf-section"><strong>Therapy Intake:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.sol_therapy_intake_note || "")}</div>`;
  }
  if (
    notesDetails.note_type === NoteType.THERAPY_FOLLOW_UP_DAP ||
    notesDetails.note_type === NoteType.THERAPY_FOLLOW_UP_SOAP
  ) {
    document += `<div class="pdf-section"><strong>Therapy Follow-up:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.sol_therapy_followup_note || "")}</div>`;
  }
  if (notesDetails.note_type === NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP) {
    document += `<div class="pdf-section"><strong>Psychiatric Follow-up:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.psych_followup_note || "")}</div>`;
  }
  if (notesDetails.note_type === NoteType.SOAP_SPEECH_THERAPY) {
    document += `<div class="pdf-section"><strong>SOAP (Speech Therapy):</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.document || "")}</div>`;
  }
  if (notesDetails.note_type === NoteType.CONSULT_NOTE) {
    document += `<div class="pdf-section"><strong>Consult Note:</strong><br/>${replaceNewLineWithBreakTag(notesDetails?.document || "")}</div>`;
  }
  if (notesDetails.note_type > NoteType.CONSULT_NOTE) {
    document += `<div class="pdf-section"><strong>${notesDetails?.note_type_info?.name || ""}:</strong><br/>${notesDetails.document}</div>`;
  }

  const html = `<html>
  <head>
    <title></title>
    <style>
      @media print {
        body { margin: 0; padding: 0; }
        @page { margin: 0.5in; }
      }
      body {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        margin: 0;
        padding: 20px;
      }
      .pdf-section {
        margin-bottom: 24px;
        page-break-inside: avoid;
        page-break-after: auto;
        overflow-wrap: break-word;
        word-break: break-word;
      }
      strong { color: #333; }
    </style>
  </head>
  <body>
    ${document}
  </body>
  </html>`;
  return html;
};

export const copyAllContent = (notesDetails: TNotes) => {
  if (!notesDetails) return;

  let copiedText = "";
  let addOnDetails = "";

  trackEvent(EventType.COPY_NOTE, {
    noteType: getNotesType(notesDetails.note_type),
    noteSection: NoteSection.ALL,
    recordingId: notesDetails?.id,
  });

  const addDetails = (detailsArray: string[]) => "\n\n" + detailsArray.join("\n\n") + "\n\n";

  if (notesDetails.location_type != null || notesDetails.duration_type != null) {
    const details = ["Session Details:"];
    if (notesDetails.location_type != null) {
      details.push(`Location: ${notesDetails.location_type === 0 ? "Telehealth" : "In-person"}`);
    }
    if (notesDetails.duration_type != null) {
      details.push(`CPT Code: ${durationTextFromType(notesDetails.duration_type)}`);
    }
    copiedText = addDetails(details);
  }

  // Replace old CPT/addon logic with new procedural codes
  const proceduralCodesText = buildProceduralCodesText(notesDetails);
  const diagnosticCodesText = buildDiagnosticCodesText(notesDetails);

  if (proceduralCodesText) {
    addOnDetails += proceduralCodesText;
  }

  if (diagnosticCodesText) {
    addOnDetails += diagnosticCodesText;
  }

  copyNote(notesDetails, copiedText, addOnDetails);
  message.success("All content copied to clipboard");
};

export const downloadNotePDF = (notesDetails: TNotes) => {
  if (!notesDetails) return;

  const html = generateHTMLForPDF(notesDetails);
  // Extract content from the generated HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const sections = Array.from(doc.querySelectorAll(".pdf-section"));
  const content = sections.map((section) => {
    const strong = section.querySelector("strong");
    const title = strong ? strong.textContent || "" : "";
    if (strong) strong.remove();

    // Preserve line breaks and formatting
    const text = section.innerHTML
      .replace(/<br\s*\/?>/gi, "\n") // Convert <br> tags to newlines
      .replace(/<[^>]*>/g, "") // Remove other HTML tags
      .replace(/&nbsp;/g, " ") // Replace &nbsp; with spaces
      .replace(/^\s*:/, "") // Remove leading colon
      .trim();

    return {
      text: [
        { text: title, style: "header" },
        { text: "\n" },
        { text: text, style: "normal", preserveLeadingSpaces: true },
      ],
      margin: [0, 0, 0, 12] as [number, number, number, number],
    };
  });

  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 14, bold: true, color: "#333" },
      normal: {
        fontSize: 11,
        lineHeight: 1.5,
        preserveLeadingSpaces: true,
      },
    },
    defaultStyle: {
      fontSize: 11,
      lineHeight: 1.5,
      preserveLeadingSpaces: true,
      font: "Roboto",
    },
    pageMargins: [40, 60, 40, 60] as [number, number, number, number],
    fonts: {
      Roboto: {
        normal: "Roboto-Regular.ttf",
        bold: "Roboto-Medium.ttf",
        italics: "Roboto-Italic.ttf",
        bolditalics: "Roboto-MediumItalic.ttf",
      },
    },
  };

  pdfMake.createPdf(docDefinition).download(`note-${notesDetails.id}.pdf`);
};
